import { Ext } from "../../Doc";
import type { GuideProps } from "../Guide";

/**
 * The member guide, Japanese translation of en.tsx. Every number comes from `limits`; every claim
 * about who can read what follows the threat model (PLAN.md 4.12). Keep it in step with en.tsx.
 */
export function GuideJa({ limits: l, link }: GuideProps) {
  return (
    <>
      <h1>Vrelisの仕組み</h1>
      <p className="lead">
        Vrelisは、暗号化されたメッセージ「タイムカプセル」を送るサービスです。カプセルは、あなたが選んだ瞬間から、試験運用の全員、または1人の相手が読めるようになります。その瞬間までは、誰もカプセルを読めません。宛先の相手も、Vrelisの運営者も、あなた自身もです。
      </p>
      <p className="warn">
        これはクローズドな試験運用です。残したいカプセルはダウンロードし（下記参照）、リカバリーフレーズを保管してください。
      </p>

      <h2>ロックが信頼できる理由</h2>
      <p>
        ロックはVrelisによる約束ではありません。公開された暗号技術と、Vrelisが管理していない公開の時刻信号によるものです。ここでは、ロックを支えるものと、ロックが破られるには何を信じる必要があるかを説明します。
      </p>

      <h3>drand：時刻に署名する公開の時計</h3>
      <ul>
        <li>
          <Ext href="https://drand.love"><b>drand</b></Ext>は、{" "}
          <Ext href="https://www.cloudflare.com/leagueofentropy/">League of Entropy</Ext>が運営するネットワークです。League of Entropyは独立した組織（Cloudflare、Protocol Labs、Kudelski Security、EPFL、University College Londonなど）のグループで、共同で3秒ごとにランダムな署名付きの値を公開しています。値には番号が付いており、ラウンド1、ラウンド2というように、それぞれが既知の瞬間に対応します。誰でも複数の公開リレーから値を読めますが、未来の値はまだ存在しないため、誰も読めません。
        </li>
        <li>
          各ラウンドは閾値暗号（<Ext href="https://en.wikipedia.org/wiki/BLS_digital_signature">BLS署名</Ext>）で署名されます。各組織は秘密鍵の断片を1つずつ持ち、署名には断片の過半数が必要です。単独の組織も、少数派の組織も、ラウンドの署名をその時刻より前に作ることはできません。このネットワークは2020年から稼働しており、私たちが使うチェーン{" "}
          <Ext href="https://api.drand.sh/v2/chains/52db9ba70e0cc0f6eaf7803dd07447a1f5477735fd3f661792ba94600c84e971/info">quicknet</Ext>は2023年から稼働しています。その公開鍵は私たちのコードに固定されており、受け取るすべての信号はその公開鍵で検証されます。
        </li>
        <li>
          <b>タイムロック暗号</b>（<Ext href="https://eprint.iacr.org/2023/189">tlock</Ext>、2023年にProtocol LabsとCloudflareの研究者が発表）は、この時計をロックに変えます。メッセージはラウンド番号に対して暗号化され、公開されたラウンドの署名だけが、それを開く鍵になります。drandが署名を公開するまで、その鍵は世界のどこにも存在しません。
        </li>
      </ul>

      <h3>Vrelisでの使い方</h3>
      <ul>
        <li>
          ブラウザーは、テキストとファイルの鍵を、あなたが選んだ時刻のdrandラウンドに対して暗号化します。「カプセルを書く」画面は、送信前にラウンド番号を表示します。非公開カプセルは受信者の鍵に対してもう一度暗号化されるため、時刻の後に開けるのは受信者だけです。
        </li>
        <li>
          サーバーは封をされた結果だけを受け取り、保存します。サーバーはカプセルを開ける鍵を持っていません。時刻の前はもちろん、非公開カプセルについては時刻の後も同じです。データベースが漏えいしても、明らかになるのは暗号文とメタデータであり、メッセージの内容ではありません。
        </li>
        <li>
          時刻が来ると、ブラウザー（またはオフライン開封ツール）が4つの公開drandリレーのいずれかからラウンドの署名を取得し、固定された公開鍵で検証して復号します。この手順にVrelisは関与しません。
        </li>
        <li>
          すべてのカプセルは作成者の鍵（<Ext href="https://en.wikipedia.org/wiki/Secp256k1">secp256k1</Ext>。Bitcoinや<Ext href="https://nostr.com">Nostr</Ext>が使うのと同じ種類の鍵）で署名されるため、変更されたカプセルは検出されます。鍵はブラウザー内で12個の単語（<Ext href="https://github.com/bitcoin/bips/blob/master/bip-0039.mediawiki">BIP-39</Ext>）から作られ、暗号化されていない状態でブラウザーの外に出ることはありません。
        </li>
      </ul>

      <h3>Vrelisがなくなった場合</h3>
      <ul>
        <li>
          フォーマットは公開されています。カプセルは<Ext href="https://github.com/nostr-protocol/nips/blob/master/01.md">Nostrイベント</Ext>（非公開カプセルの場合は{" "}
          <Ext href="https://github.com/nostr-protocol/nips/blob/master/59.md">NIP-59 gift wrap</Ext>）で、その内容は{" "}
          <Ext href="https://age-encryption.org">age</Ext>ファイルです。このファイルには<Ext href="https://github.com/drand/tlock-js">tlock</Ext>スタンザが含まれます。これらのオープンな仕様を実装したソフトウェアなら、どれでもカプセルを開けます。私たちのソフトウェアは必要ありません。
        </li>
        <li>
          <b>ダウンロード</b>を使うと、カプセルをオフライン開封ツールと一緒に自分のディスクに保管できます。オフライン開封ツールは1つのHTMLファイルで、Vrelisのサーバーなしで、必要なdrandの信号と（非公開カプセルの場合は）あなたの鍵を使い、コンピューター上でカプセルを開きます。必要な信号が入手でき、必要なファイルと鍵を保持していれば、完全なエクスポートはvrelis.comなしで開けます。
        </li>
        <li>
          必要な未来の信号を生成する前にdrand自体が停止した場合、それらのカプセルは永久にロックされたままになる可能性があります。事前にダウンロードしても、このリスクはなくなりません。開封日を過ぎたカプセルでも、検証済みの正確な信号が必要です。その信号がアーカイブに含まれておらず、リレーからも入手できない場合、日付だけではオフラインで開けません。完全なエクスポートと、それに必要な鍵を保管してください。
        </li>
      </ul>

      <h3>ロックが破られるには何を信じる必要があるか</h3>
      <ul>
        <li>
          drandの組織の過半数が共謀して、ラウンドに早く署名すること。各組織は独立しており、複数の国にあり、チェーンは公開されているため、そのような署名は誰の目にも見えます。これがシステム全体の信頼の前提です。
        </li>
        <li>
          Webページが嘘をつくこと。ページはVrelisがブラウザーに配信するコードです。そのコードを管理する者は、封をする前のテキストや、ロック解除後の鍵を読めてしまいます。だからこそ、カプセルはダウンロードしてオフラインで開けるようになっており、鍵のフィンガープリントはVrelisの外で照合できるよう表示され、鍵をWebページの外に置くスマートフォンアプリが試験運用後に計画されています。
        </li>
        <li>
          現在の暗号技術が破られること。drandとあなたの鍵の両方が使う楕円曲線暗号は、将来の{" "}
          <Ext href="https://en.wikipedia.org/wiki/Post-quantum_cryptography">量子コンピューター</Ext>によって破られる可能性があります。何十年も封をしておくカプセルにとって、これは試験運用では解決できない現実のリスクです。
        </li>
      </ul>

      <h2>参加方法</h2>
      <ol>
        <li>
          管理者があなたのアドレスを承認している場合は、そのアドレスで直接登録してください。そうでない場合は、招待リンクを開き、そのアドレスを使ってください。そのGoogleアカウントでログインするか（GoogleがVrelisに伝えるのは名前とメールアドレスだけです）、パスワードでログインします（アドレス確認のためのリンクをメールで送ります）。後で、ログイン用のパスキーを追加できます。
        </li>
        <li>
          Vrelisは、ブラウザー内であなたの<b>鍵</b>を作成するよう提案します。鍵はあなたの主な署名で、あなたが送るものに署名し、あなたに送られた非公開カプセルを開きます。スキップして、後でアカウントの「鍵」で作成することもできます。鍵がない間は、公開カプセルしか読めません。<b>リカバリーフレーズ</b>（どの端末でも鍵を再構築できる12個の単語）が表示される前に、誰も画面を見たり記録したりできないことを確認するよう求められます。
        </li>
        <li>12個の単語を紙に書いてください。そのうち3つを入力し直し、次に本人確認のためにもう一度ログインするよう求められます。確認にはパスワードかパスキーを使います。最初の鍵の場合に限り、Googleでも確認できます。</li>
        <li>
          PIN（{l.pinDigits}桁以上）を決め、<Ext href="https://passkeys.dev/docs/intro/what-are-passkeys/">パスキー</Ext>（指紋、顔、または端末のPIN）を作成してください。この2つで、この端末の鍵と、新しい端末への移行用にVrelisが保管するコピーを保護します。ブラウザーがこの方法でパスキーを使えない場合、鍵はそのブラウザー内で保護されないまま保存され、サーバー上にコピーは作られません。その場合、別の端末に移る方法はフレーズだけです。
        </li>
      </ol>

      <h2>リカバリーフレーズ</h2>
      <ul>
        <li>紙に書いて、安全な場所に保管してください。写真を撮ったり、コンピューターやパスワードマネージャーに保存したりしないでください。</li>
        <li>
          12個の単語を持つ人は、あなた<b>そのもの</b>です。あなたとして署名し、あなたの非公開カプセルを開けます。単語が漏えいした場合は、アカウントの「鍵」で<b>鍵をリセット</b>を使ってください。フレーズを入力してもう一度ログインすると、鍵は永久に取り消され、誰もその鍵で送ることも、その鍵宛てに送ることもできなくなります。いつから盗まれた可能性があるかを指定すると、それ以降にその鍵で送られたカプセルには「取り消された鍵」と表示されます。その後、新しいフレーズで新しい鍵を作成します。
        </li>
        <li>
          以前の鍵は、現在の鍵で暗号化された<b>キーリング</b>に入れられるため、古い鍵宛てに送られたカプセルも古いフレーズなしで開けます。古いフレーズを自分で保管し、後でキーリングに追加することもできます。
        </li>
        <li>
          端末を失くした場合：別の端末でログインし、パスキーとPIN（パスキーが同期される場合）またはフレーズを使ってください。PINを忘れた場合：フレーズを使ってください。PINを{l.pinAttempts}回間違えるとサーバー上のコピーはブロックされ、フレーズで新しいコピーが書き込まれます。
        </li>
        <li>
          フレーズ<b>と</b>、鍵のあるすべての端末を失った場合、あなたの非公開カプセルは誰にも、二度と開けません。管理者にできるのは、新しいIDを与えることだけです。鍵の取り消しにもフレーズが必要なため、フレーズがない場合は管理者が代わりに取り消します。
        </li>
      </ul>

      <h2>カプセルを書く</h2>
      <ul>
        <li>
          <b>試験運用の全員</b>（公開）または<b>1人のメンバー</b>（非公開）。公開カプセルは時刻の後、すべてのメンバーが読めます。非公開カプセルを読めるのは、宛先の相手と、控えを残した場合のあなただけです（控えは初期設定でオン）。
        </li>
        <li>
          すべてのカプセルには<b>件名</b>が必要です。件名は暗号化されず、カプセルを見た人はすぐに読めます。サーバーも同じです。
        </li>
        <li>
          <b>紹介文</b>は任意です。最大{l.introChars}文字で、時刻の前でもカプセルをクリックすると読めます。公開カプセルでは全員が読み、サーバーも読めます。非公開カプセルでは受信者だけが自分の鍵で読み、サーバーには見えません。どちらの場合も、メッセージとファイルはロックされたままです。
        </li>
        <li>
          非公開カプセルの送信者は、受信者の鍵に封をされます。受信者は、時刻よりずっと前でも、自分の鍵でカプセルを開けばすぐに送信者がわかります。その鍵を持たずにカプセルを手にした人には、件名と時刻しか見えません。サーバーは、どのアカウントが送ったかを知っています。サーバーはそれを受信者の鍵に暗号化して受信者だけに伝え、カプセルに署名した鍵がそのアカウントのものかをアプリが確認できるようにします。一致しない場合、名前の代わりに「未確認の送信者」と表示されます。
        </li>
        <li>
          <b>送信者名を隠す</b>（非公開カプセル）：受信者の一覧では、受信者がカプセルをクリックするまで、あなたの名前の代わりに「送信者非表示」と表示されます。そのため、受信者の後ろから画面をのぞく人には、誰が書いたかわかりません。受信者自身は、時刻の前でもあなただとわかります。
        </li>
        <li>
          開封時刻を選びます：5分後、1時間後、1日後、1週間後、1年後、または2125年までの日付。実際の開封時刻は送信前に表示され、数秒単位で正確です。
        </li>
        <li>
          テキスト：公開カプセルは最大{l.publicText}、非公開カプセルは最大{l.privateText}。ファイル：最大{l.files}ファイル、合計でフリーは{l.fileBytes}、レガシーまたはスタートは{l.planFileBytes}まで。写真は縮小され、位置情報などのメタデータが除かれます。
        </li>
        <li>
          <b>封をして送信</b>は、カプセルに署名するためにPINとパスキーを1回求めます。その後は誰もカプセルを変更できず、時刻の前に読むこともできません。
        </li>
        <li>
          非公開で誰かに書く前に、相手の鍵を確認してください。「カプセルを書く」画面には鍵のフィンガープリント（16文字）が表示されます。このブラウザーが前回確認してから相手の鍵が変わった場合は警告が表示され、新しい鍵が本当に相手のものだとVrelisの外で相手と確認するまで、送信を待ちます。
        </li>
      </ul>

      <h2>読む</h2>
      <ul>
        <li>
          <b>公開メッセージ</b>：公開カプセル。<b>受信トレイ</b>：あなた宛ての非公開カプセル。<b>送信済み</b>：あなたのカプセル。<b>ウォッチ中</b>：あなたが待っている公開カプセル。作成者をフォローすると、その人のカプセルだけを表示できます。
        </li>
        <li>
          メンバーを<b>非表示</b>にすると、その人の公開カプセルは、すべての端末であなたの公開メッセージから外れます。相手には何も変わりません。メンバーを<b>ブロック</b>すると、その人はあなたの受信トレイに非公開カプセルを届けられなくなります（「カプセルを書く」画面が送信前にそのことを伝えます）。あなたは引き続きその人をフォローし、公開カプセルを読めます。その人の以前のカプセルは受信トレイの「ブロック中」に移り、引き続き開けます。どちらの操作も、その人のメニューと「ユーザー」ページにあり、このページにはあなたのフォロワーも表示されます。Vrelisが保証できるのは、Vrelisが受け取って配信するものだけです。Vrelisの外であなたの公開鍵に対して暗号化することは、誰にも止められません。
        </li>
        <li>
          非公開カプセルは、開くまで何も表示しません。<b>開く</b>はPINを求め、その後、送信者と開封日時を表示します。テキストは開封日時が来てから表示します。
        </li>
        <li>
          鍵はロックされたままで、鍵が必要な操作のたびにPINを求められます。続けていくつかのカプセルを読むには、上部のバーにある<b>…分間ロック解除</b>を使ってください。<b>ロック</b>すると、非公開のものがすべて一度に隠れます。ページを離れた場合も同じです。
        </li>
        <li>
          <a href="/account">アカウント</a>の「プライバシー」で「一覧の送信者を、カプセルをクリックした後にだけ表示する」をオンにすると、すべての端末で、どの非公開カプセルも送信者名を隠したものとして扱われます。
        </li>
        <li>
          一覧には一度に50件のカプセルが表示され、<b>さらに表示</b>で次のカプセルが追加されます。Vrelisがカプセルを絞り込み、並べ替えます（開封日時順、またはVrelisが受け取った日時順）。そのため、絞り込みやグループを使うと、選んだメンバーの識別子がVrelisに送られます。グループそのものやグループ名が送られることはありません。受信トレイを送信者で絞り込むには鍵が必要です。この絞り込みでは送信者名を隠したカプセルが除外され、送信者をクリック後にだけ表示する設定の間は使えません。
        </li>
        <li>
          <b>検索</b>の対象は、件名、公開カプセルの紹介文、公開カプセルの作成者、あなたが送った相手です。開封したテキスト（あなたの端末にしかありません）や、非公開カプセルの送信者は検索しません。
        </li>
      </ul>

      <h2>返信用封筒、CC、やり取り</h2>
      <ul>
        <li>
          非公開のメッセージには<b>返信用封筒</b>を付けられます。各受信者に、あなたの鍵に封をした非公開の返信を1通ずつ依頼するものです。受信者は、あなたをフォローしていなくても返信できます。すべての返信を、あなたが設定した時刻に開く（その時刻を過ぎると返信は受け付けられません）か、受信者が選んだ時刻に開くかを選べます。カプセルと返信の代金は前払いで、受信者1人につき1ではなく2スタンプです。受信者の最初の返信は無料です。2通目にはその人のスタンプが1つかかり、あなたには両方が見えます。
        </li>
        <li>
          <b>CC</b>：1人のメンバーへの非公開のメッセージを、同時に他のメンバーにも送れます。一覧に載った全員が、他に誰が受け取ったかを見られます。一覧は送信者の署名とともにメッセージの中に封をされ、サーバーが見ることはありません。CCなしのグループ送信では受信者が伏せられたままで、他に誰が受け取ったかは誰にも見えません。
        </li>
        <li>
          返信用封筒付きのメッセージとその返信は、受信トレイや送信済みではなく<b>やり取り</b>ページにあります。各依頼の下に、すべての受信者の返信が順番に、それぞれの時刻までロックされた状態で表示され、誰が支払ったかもわかります。返信は送信者だけに届きます。
        </li>
      </ul>

      <h2>Vrelisなしでカプセルを保管する</h2>
      <ul>
        <li>
          カプセルの<b>ダウンロード</b>で、カプセルをファイルとして保存できます。添付ファイルがある場合は、ファイルとオフライン開封ツールを含む.zipになります。複数を選んで{" "}
          <b>選択したものをダウンロード</b>を使うと、1つのアーカイブになります（「表示中のものをすべて選択」で、表示中のカプセルを選べます）。
        </li>
        <li>
          オフライン開封ツールは1つのHTMLファイルです（<a href="/account">アカウント</a>、または.zipの中）。自分のコンピューター上でカプセルを開き、非公開カプセルはフレーズで開きます。vrelis.comには一切接続しません。ファイルにまだ開封の信号が含まれていない場合にだけ、公開のdrand時刻サービスに信号を問い合わせます。
        </li>
        <li>
          「開封したテキストとファイルを含める」を使うと、開封したテキストとファイルが暗号化されずにダウンロードに含まれます。そのファイルを持つ人なら誰でも読めます。
        </li>
      </ul>

      <h2>この端末</h2>
      <ul>
        <li>
          <b>ロック</b>：鍵をメモリから消します。<b>ログアウト</b>：このセッションを終了します。鍵はこの端末に残ります。<b>すべての端末からログアウト</b>：すべての端末のセッションを終了します。<b>この端末のデータを削除</b>：鍵と、Vrelisがこのブラウザーに保存したすべてを削除します。
        </li>
        <li>ログインは{l.sessionDays}日間有効です。</li>
      </ul>

      <h2>Vrelisに見えるもの、見えないもの</h2>
      <ul>
        <li>
          <b>Vrelisが読めないもの</b>：時刻前のカプセル、非公開カプセル（いつまでも。Vrelisはその鍵を持っていません）、あなたの鍵、PIN、フレーズ。
        </li>
        <li>
          <b>Vrelisに見えるもの</b>：名前とメールアドレス（メールアドレスはオーナーと管理者に見えます）、誰がカプセルを誰に送ったか、いつ送ったか、公開カプセルがいつ開くか、サイズ、カプセルにファイルがあること。時刻の後、公開カプセルはすべてのメンバーが読めるため、Vrelisも読めます。
        </li>
        <li>
          キーリングと鍵のコピーは、暗号化された状態でのみ保存されます。Vrelisはそれらを開けません。
        </li>
        <li>
          署名は、どの鍵がカプセルを書いたかを証明します。「取り消された鍵」と表示されたカプセルは、所有者が後に盗まれたと報告した鍵で署名されています。その鍵がある人のものであることは、メンバー一覧と、送信者がカプセルに入れた名前に基づいています。非公開で書く相手とは、フィンガープリントを照合してください。
        </li>
      </ul>

      <h2>プランとスタンプ</h2>
      <ul>
        <li>
          1つの<b>スタンプ</b>で、1人の受信者に1回配信できます。控えは無料です。フリーでは毎月{l.freeStamps}スタンプ、ストレージ{l.freeStorage}、1メッセージあたり{l.fileBytes}のファイルが使えます。レガシー（毎月{l.legacyStamps}スタンプ、{l.planStorage}、{l.planFileBytes}）は最初のメンバーに付与されました。スタートなら毎月{l.planStamps}スタンプになります。その月のスタンプは繰り越されず、翌月の1日（UTC）に補充されます。<b>お支払い</b>ページには、持っているもの、残っているもの、予定されているものが表示されます。
        </li>
        <li>
          現時点では何も請求されず、何も購入できません。プランはプレビューで、依頼は管理者が手動で有効にします。すでに送ったものは、プランやスタンプがどうなっても、あなたと送信先の人が読める状態のままです。
        </li>
        <li>
          自分用の控えは任意で、初期設定でオンです。オフにすると、そのメッセージを読めるのは受信者だけになります。開封後も含め、あなた自身は読めません。
        </li>
      </ul>

      <h2>試験運用の制限</h2>
      <ul>
        <li>
          配信は1日あたり最大{l.daily}回です（控えは無料。グループへのカプセルはメンバーごとに1回と数えます）。アップロード：24時間あたり{l.uploadDaily}と未使用ファイル{l.unusedFiles}個まで。24時間以内にカプセルで使われなかったファイルは削除されます。
        </li>
        <li>Google、パスワード、またはパスキーでログインします。支払いはなく、公開ネットワークもありません。カプセルは試験運用の中にとどまります。</li>
        <li>
          すでにアカウントを持つアドレスを招待すると、代わりにそのメンバーへつながりのリクエストが送られます。相手が承諾すると、互いにフォローします。どちらの場合もあなたの画面は同じなので、招待を使って誰がメンバーかを調べることはできません。
        </li>
      </ul>

      <h2>開発者向け</h2>
      <p>
        サービスは<a href="/developers">Vrelis API</a>を通じてカプセルを読み取り、送信できます。サービスは管理者からのサービス招待で専用のアカウントを取得し、他の全員には通常のメンバーとして見えます。サービスは自分の鍵を作成し、すべてのカプセルに自分で署名します。サービスのAPIトークンは読み取りと送信ができますが、鍵やログイン方法は管理できません。サービスも、他の人と同じようにブロックしたり非表示にしたりできます。
      </p>

      <h2>試験運用メンバーへのお願い</h2>
      <ol>
        <li>1時間後に開く公開カプセルを1つと、1日後に開く、他のメンバーへの非公開カプセルを1つ送ってください。</li>
        <li>公開カプセルを開封後に読み、開封前にも読もうとしてみてください。読めないはずです。</li>
        <li>どなたか1人：2台目の端末で鍵を設定し、そこで受信トレイを読んでください。</li>
        <li>どなたか1人：他のメンバーを非表示にし、その人のカプセルがあなたの公開メッセージからだけ消えることを確認してください。</li>
      </ol>

      <h2>問題があるとき</h2>
      <p>
        各ページの下部にある<b>問題を報告</b>を使うか、{link}までご連絡ください。何をしたか、何を期待したか、何が起きたかを書いてください。スクリーンショットがあると助かります。サービス、その制限と責任については<a href="/terms">利用規約</a>に記載しています。
      </p>
    </>
  );
}
